import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, Sparkles, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: "Pending", color: "text-slate-500 bg-slate-100", Icon: Clock },
  pending_review: { label: "Pending review", color: "text-amber-700 bg-amber-100", Icon: Clock },
  paid: { label: "Paid", color: "text-forest bg-forest/10", Icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "text-red-700 bg-red-100", Icon: XCircle },
};

export default function AdminManagerSubscriptions() {
  const [filter, setFilter] = useState("pending_review");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [runningSweep, setRunningSweep] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await axios.get(`${API}/admin/manager-subscriptions`, { params });
      setRows(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load subscriptions");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (sub) => {
    if (!window.confirm(`Mark ${sub.email || sub.user_id}'s ${sub.plan} subscription as paid?\n\nA welcome receipt will be emailed automatically.`)) return;
    setPayingId(sub.id);
    try {
      await axios.post(`${API}/admin/manager-subscriptions/${sub.id}/mark-paid`);
      toast.success("Marked paid — welcome email queued");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark paid");
    } finally {
      setPayingId(null);
    }
  };

  const runSweep = async () => {
    setRunningSweep(true);
    try {
      const { data } = await axios.post(`${API}/admin/manager-subscriptions/run-renewal-sweep`);
      toast.success(`Renewal sweep: ${data.sent} sent · ${data.found} due`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed");
    } finally {
      setRunningSweep(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-manager-subscriptions">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-copper" />
            <div className="text-xs font-bold uppercase tracking-widest text-copper">EasyStay Manager</div>
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Subscriptions</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Hosts appear here after confirming their Mobile Money payment. Verify the reference against your Mobile Money statement, then hit "Mark as Paid" to activate the plan and send the welcome receipt.
          </p>
        </div>
        <button
          onClick={runSweep}
          disabled={runningSweep}
          data-testid="run-renewal-sweep"
          className="flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          title="Fire the 7-day renewal-reminder cron on demand"
        >
          {runningSweep ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Run renewal sweep now
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="subs-filters">
        {["pending_review", "paid", "pending", "cancelled", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
              filter === f
                ? "border-copper bg-copper/10 text-copper"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {f === "all" ? "All" : STATUS_META[f]?.label || f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-12 text-center text-sm text-slate-500">
          No subscriptions in this state.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((sub) => {
            const meta = STATUS_META[sub.status] || { label: sub.status, color: "text-slate-500 bg-slate-100", Icon: Clock };
            const Icon = meta.Icon;
            return (
              <div
                key={sub.id}
                data-testid={`sub-row-${sub.id}`}
                className="grid grid-cols-1 gap-3 rounded-3xl bg-white p-4 card-shadow sm:grid-cols-[1fr,120px,150px,auto] sm:items-center"
              >
                <div className="min-w-0">
                  <div className="truncate font-semibold text-slate-900">{sub.email || sub.host_name || sub.user_id}</div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span>Ref: <span className="font-mono">{sub.payment_ref || "—"}</span></span>
                    <span>{sub.payment_method?.toUpperCase()}</span>
                    {sub.confirmed_at && <span>Confirmed {new Date(sub.confirmed_at).toLocaleString()}</span>}
                    {sub.paid_at && <span>Paid {new Date(sub.paid_at).toLocaleString()}</span>}
                  </div>
                </div>
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-widest text-slate-400">Plan</div>
                  <div className="font-bold capitalize text-slate-900">{sub.plan}</div>
                  <div className="text-xs text-slate-600">ZMW {(sub.amount_zmw || 0).toLocaleString()}</div>
                </div>
                <div className={`inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-1 text-xs font-bold ${meta.color}`}>
                  <Icon className="h-3 w-3" />
                  {meta.label}
                </div>
                <div>
                  {sub.status === "pending_review" ? (
                    <button
                      onClick={() => markPaid(sub)}
                      disabled={payingId === sub.id}
                      data-testid={`mark-paid-${sub.id}`}
                      className="flex items-center gap-1.5 rounded-full bg-forest px-4 py-2 text-xs font-semibold text-white hover:bg-forest/90 disabled:opacity-50"
                    >
                      {payingId === sub.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      Mark as Paid
                    </button>
                  ) : (
                    <div className="text-xs text-slate-400">—</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
